import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import Close from "assets/images/icons/JSX/Close";
import { actions as userActions } from "store/users";
import {
    FORCE_TOURS,
    TOUR_REPLAY_HINT_META_KEY,
    TOUR_REPLAY_HINT_SHOW,
    isTourShown,
} from "utils/tours";

import classes from "features/Model/Enclosure/RoomViewKeyboardHint/styles.module.scss";

const { saveUserMeta } = userActions;

const SHOW_DELAY_MS = 300;
const DISMISS_DURATION_MS = 10000;
const EXIT_ANIMATION_MS = 300;

let hintHasRun = false;

export default function TourReplayHint() {
    const dispatch = useDispatch();
    const uid = useSelector((state) => state.user?.uid);
    const hintShown = useSelector((state) => state.user?.userMeta?.[TOUR_REPLAY_HINT_META_KEY]);

    const [visible, setVisible] = useState(false);
    const [closing, setClosing] = useState(false);
    const [showToken, setShowToken] = useState(0);
    const timersRef = useRef({});
    const closeHintRef = useRef(() => {});
    const hintShownRef = useRef(hintShown);

    hintShownRef.current = hintShown;

    useEffect(() => {
        const showHint = () => {
            if (isTourShown(hintShownRef.current) || hintHasRun) return;

            clearTimeout(timersRef.current.show);
            clearTimeout(timersRef.current.dismiss);
            clearTimeout(timersRef.current.exit);
            setClosing(false);
            setShowToken((token) => token + 1);
        };

        window.addEventListener(TOUR_REPLAY_HINT_SHOW, showHint);
        return () => window.removeEventListener(TOUR_REPLAY_HINT_SHOW, showHint);
    }, []);

    useEffect(() => {
        if (!showToken) return undefined;

        let cancelled = false;
        let persisted = false;

        const persistShown = () => {
            if (persisted || !uid || FORCE_TOURS) return;
            persisted = true;
            dispatch(saveUserMeta({ uid, userMeta: { [TOUR_REPLAY_HINT_META_KEY]: true } }));
        };

        const closeHint = () => {
            clearTimeout(timersRef.current.dismiss);
            clearTimeout(timersRef.current.exit);
            persistShown();
            setClosing(true);
            timersRef.current.exit = setTimeout(() => setVisible(false), EXIT_ANIMATION_MS);
        };

        closeHintRef.current = closeHint;

        timersRef.current.show = setTimeout(() => {
            if (cancelled) return;

            hintHasRun = true;
            persistShown();
            setVisible(true);

            timersRef.current.dismiss = setTimeout(() => {
                if (cancelled) return;
                closeHint();
            }, DISMISS_DURATION_MS);
        }, SHOW_DELAY_MS);

        return () => {
            cancelled = true;
            clearTimeout(timersRef.current.show);
            clearTimeout(timersRef.current.dismiss);
            clearTimeout(timersRef.current.exit);
            // Keep hintHasRun so later tour completions do not re-show the tip.
        };
    }, [showToken, dispatch, uid]);

    if (!visible) return null;

    return createPortal(
        <div
            className={[classes.toast, classes.aboveFeedback, closing ? classes.exit : classes.enter].join(" ")}
            role="status"
            aria-live="polite"
        >
            <div className={classes.card}>
                <div className={classes.content}>
                    <div className={classes.contentHeader}>
                        <div className={classes.title}>Guided tour</div>
                        <button
                            type="button"
                            className={classes.closeBtn}
                            onClick={() => closeHintRef.current()}
                            aria-label="Dismiss guided tour hint"
                        >
                            <Close />
                        </button>
                    </div>
                    <div className={classes.body}>
                        To see a tour again, turn it on under Guided Tours on your Account page.
                    </div>
                </div>
                <div className={classes.progressTrack}>
                    <div key={showToken} className={classes.progressBar} />
                </div>
            </div>
        </div>,
        document.body
    );
}
